import images from '@assets/images/index'
import React, { useCallback } from 'react'

import { Button } from '@suntex.fim/ui'

interface ScrollToTopButtonProps {
  /** Optional target selector for the scrollable container. Defaults to '.ant-layout-content' */
  targetSelector?: string
  /** Optional className for the scroll-to-top container */
  className?: string
  /** Optional className for the button */
  buttonClassName?: string
  /** Optional onClick handler to execute additional logic before scrolling */
  onBeforeScroll?: () => void
  /** Optional onClick handler to execute additional logic after scrolling */
  onAfterScroll?: () => void
}

/**
 * Reusable scroll-to-top button component
 * Provides smooth scrolling to the top of the page or a specific container
 */
const ScrollToTopButton: React.FC<ScrollToTopButtonProps> = ({
  targetSelector = '.ant-layout-content',
  className = 'scroll-to-top',
  buttonClassName = 'scroll-button',
  onBeforeScroll,
  onAfterScroll
}) => {
  const scrollToTop = useCallback(
    (event?: React.MouseEvent) => {
      if (event) {
        event.preventDefault()
        event.stopPropagation()
      }
      onBeforeScroll?.()
      const contentElement = document.querySelector(targetSelector)
      if (contentElement) {
        contentElement.scrollTo({ top: 0, behavior: 'smooth' })
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' })
        document.documentElement.scrollTop = 0
        document.body.scrollTop = 0
      }
      onAfterScroll?.()
    },
    [targetSelector, onBeforeScroll, onAfterScroll]
  )

  return (
    <div className={className}>
      <Button
        className={buttonClassName}
        onClick={scrollToTop}
        icon={<images.ArrowUp />}
        aria-label="Scroll to top"
      />
    </div>
  )
}

export default ScrollToTopButton
